import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  getActivationStatus,
  getJobStatus,
  startAgentSession,
  sendSessionMessage,
  getSessionHistory,
  getWorkspace,
  getBillingPlans,
} from '../api';
import { pollJobStatus } from '../utils/jobPolling';
import { useJobActivity } from '../hooks/useJobActivity';
import type { ActivityStep } from '../hooks/useJobActivity';
import { isInstagramConnected } from '../utils/instagram';
import { AgentCardList, parseAgentOutput } from '../components/AgentCards';
import { InstagramConnectInline } from '../components/InstagramConnectInline';
import type { AgentAction, AgentMessage, PageKey, ThemeColors, Workspace } from '../types';
import { track } from '../analytics';
import { metaTrack, onceEver, planValue, planCurrency } from '../metaPixel';
import './ActivationPage.css';

type ActivationPageProps = {
  workspaceId: number;
  onNavigate: (_page: PageKey) => void;
  onWorkspaceUpdate?: (_workspace: Workspace) => void;
};

// ─── Plan config (used for the handoff screen copy) ──────────────────────────

interface PlanConfig {
  name: string;
  emoji: string;
  tagline: string;
  outlineCount: number;
  hasImage: boolean;
}

const PLAN_CONFIGS: Record<string, PlanConfig> = {
  starter: {
    name: 'Starter',
    emoji: '🌱',
    tagline: 'Strategy, identity & scheduled post briefs',
    outlineCount: 2,
    hasImage: false,
  },
  creator: {
    name: 'Creator',
    emoji: '✨',
    tagline: 'Full brand build + first post ready to publish',
    outlineCount: 5,
    hasImage: true,
  },
  pro: {
    name: 'Pro',
    emoji: '🚀',
    tagline: 'Full brand build + first post ready to publish',
    outlineCount: 7,
    hasImage: true,
  },
  developer: {
    name: 'Developer',
    emoji: '⚡',
    tagline: 'Full brand build + first post ready',
    outlineCount: 5,
    hasImage: true,
  },
};

const DEFAULT_PLAN_CONFIG: PlanConfig = {
  name: 'Conistra',
  emoji: '✨',
  tagline: 'Your brand, built with you',
  outlineCount: 3,
  hasImage: false,
};

function getPlanConfig(code: string): PlanConfig {
  return PLAN_CONFIGS[code] ?? DEFAULT_PLAN_CONFIG;
}

// Kickoff sentinel — the agent treats [KICKOFF] as a system opener, and the
// history hydration hides it from the transcript.
const KICKOFF_MESSAGE =
  '[KICKOFF] The user just completed payment and landed in onboarding. Open the session.';

// ─── Message types ────────────────────────────────────────────────────────────

interface ActivationMessage extends AgentMessage {
  agentActions?: AgentAction[];
  suggestedReplies?: string[];
  done?: boolean;
  jobId?: string;
}

interface AgentOutputParsed {
  message?: string;
  actions?: AgentAction[];
  suggested_replies?: string[];
  done?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function LoadingDots() {
  return (
    <span className="act-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
  );
}

const WORKING_HINTS = ['Thinking…', 'Checking the landscape…', 'Working on your brand…'];

/** The bubble shown while a turn runs.
 *
 * Once the agent starts calling tools we show what it is actually doing; until
 * then (and for turns that answer without touching a tool) we fall back to the
 * rotating hints, which are the only honest thing we can say about a model
 * that is still thinking. */
function PendingBubble({ steps }: { steps: ActivityStep[] }) {
  const [hintIdx, setHintIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHintIdx((i) => (i + 1) % WORKING_HINTS.length), 6000);
    return () => clearInterval(id);
  }, []);

  if (steps.length > 0) {
    return (
      <div className="act-bubble act-bubble--pending">
        <div className="act-feed" aria-live="polite">
          {steps.map((step) => (
            <div
              key={step.callId}
              className={`act-feed-item ${step.done ? 'act-feed-item--done' : 'act-feed-item--active'}`}
            >
              <span className="act-feed-item__dot" />
              <span className="act-feed-item__label">{step.label}</span>
              {!step.done && <span className="act-feed-item__spinner" />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="act-bubble act-bubble--pending">
      <div className="act-typing-row">
        <div className="act-typing">
          <span />
          <span />
          <span />
        </div>
        <span className="act-typing-context">{WORKING_HINTS[hintIdx]}</span>
      </div>
    </div>
  );
}

// ─── Brand canvas ─────────────────────────────────────────────────────────────

interface CanvasSlot {
  key: string;
  label: string;
  emoji: string;
  filled: boolean;
  thumb?: string;
  round?: boolean;
  colors?: ThemeColors | null;
}

/** The three dots next to the Colors slot — the image palette if the account has one. */
function canvasDots(colors: ThemeColors): string[] {
  const paint = (colors.image_palette || [])
    .map((c) => c?.hex)
    .filter((hex): hex is string => typeof hex === 'string' && hex.startsWith('#'));
  if (paint.length) return paint.slice(0, 3);
  return [colors.primary_500, colors.primary_700, colors.accent_500].filter(Boolean);
}

function BrandCanvas({ slots, planConfig }: { slots: CanvasSlot[]; planConfig: PlanConfig }) {
  // On phones the canvas collapses to a one-line progress strip. The chat is
  // what the user came for — the checklist is reference, available on tap.
  // Wide screens keep the full rail; there is room for it there.
  const [expanded, setExpanded] = useState(false);
  const filledCount = slots.filter((s) => s.filled).length;
  const percent = Math.round((filledCount / slots.length) * 100);

  return (
    <aside className={`act-sidebar act-canvas${expanded ? ' act-canvas--expanded' : ''}`}>
      {/* Phones: the whole canvas as one ~44px row. Tap anywhere to unfold. */}
      <button
        type="button"
        className="act-canvas__summary"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="act-canvas-body"
      >
        <span className="act-canvas__summary-label">Your brand</span>
        <span className="act-canvas__summary-count">
          {filledCount}/{slots.length}
        </span>
        <span className="act-canvas__summary-track" aria-hidden="true">
          <span style={{ width: `${percent}%` }} />
        </span>
        <span className="act-canvas__summary-caret" aria-hidden="true">
          ⌄
        </span>
      </button>

      <div className="act-canvas__body" id="act-canvas-body">
        <div className="act-canvas__head">
          <div className="act-canvas__plan">
            {planConfig.emoji} {planConfig.name} plan
          </div>
          <div className="act-canvas__title">Your brand</div>
          <div className="act-canvas__hint">Fills in as you talk — everything is optional.</div>
        </div>
        <div className="act-canvas__slots">
          {slots.map((slot) => (
            <div
              key={slot.key}
              className={`act-canvas-slot${slot.filled ? ' act-canvas-slot--filled' : ''}`}
            >
              {slot.thumb ? (
                <img
                  src={slot.thumb}
                  alt=""
                  className={`act-canvas-slot__thumb${slot.round ? ' act-canvas-slot__thumb--round' : ''}`}
                />
              ) : (
                <span className="act-canvas-slot__emoji">{slot.emoji}</span>
              )}
              <span className="act-canvas-slot__label">{slot.label}</span>
              {slot.colors && (
                <span className="act-canvas-slot__dots">
                  {canvasDots(slot.colors).map((c, i) => (
                    <span key={i} style={{ background: c }} />
                  ))}
                </span>
              )}
              <span className="act-canvas-slot__state">{slot.filled ? '✓' : ''}</span>
            </div>
          ))}
        </div>
        <div className="act-canvas__progress">
          <div className="act-canvas__progress-bar" style={{ width: `${percent}%` }} />
        </div>
      </div>
    </aside>
  );
}


// ─── Main component ───────────────────────────────────────────────────────────

function ActivationPage({ workspaceId, onNavigate, onWorkspaceUpdate }: ActivationPageProps) {
  const [phase, setPhase] = useState<
    'checking' | 'payment_pending' | 'session' | 'success' | 'error'
  >('checking');
  const [planCode, setPlanCode] = useState('creator');

  const [messages, setMessages] = useState<ActivationMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initializingRef = useRef(false);
  // Step 1 re-runs on every retry; this keeps the activation event fired once.
  const activationTrackedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const planConfig = getPlanConfig(planCode);
  const isComplete = phase === 'success';

  // The turn in flight, if any — its job feeds the pending bubble's live trail.
  const activitySteps = useJobActivity(
    messages.find((m) => m.status === 'pending')?.jobId ?? null
  );

  const refreshWorkspace = useCallback(() => {
    getWorkspace(workspaceId)
      .then((ws) => {
        setWorkspace(ws);
        onWorkspaceUpdate?.(ws);
      })
      .catch(() => {});
  }, [workspaceId, onWorkspaceUpdate]);

  const handleThemeApplied = useCallback(
    (_colors: ThemeColors) => refreshWorkspace(),
    [refreshWorkspace]
  );

  // Scroll messages to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  /**
   * Report the paid conversion to Meta, with the real price attached.
   *
   * The activation-status endpoint returns a plan code but no price, so the
   * plan list is fetched purely to put a `value` on the event. That matters more
   * than it looks: without it every plan converts identically in Ads Manager, so
   * ROAS is unreadable and value-based optimisation has nothing to work with. If
   * the fetch fails we still report the Purchase — a conversion with no value
   * beats no conversion at all.
   */
  const reportPurchase = useCallback((paidPlanCode: string) => {
    const send = (price?: number, currency?: string) => {
      metaTrack('Purchase', {
        content_name: paidPlanCode,
        content_type: 'product',
        content_category: 'subscription',
        value: planValue(price),
        currency: planCurrency(currency),
      });
    };
    getBillingPlans()
      .then((plans) => {
        const matched = plans.find((plan) => plan.code === paidPlanCode);
        send(matched?.price_cents, matched?.currency);
      })
      .catch(() => send());
  }, []);

  // ── Step 1: check subscription status ────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        const status = await getActivationStatus(workspaceId);
        setPlanCode(status.plan_code || 'creator');

        const isActive =
          status.subscription_status === 'active' ||
          status.subscription_status === 'trialing' ||
          status.subscription_status === '' ||
          status.subscription_status === 'none';

        if (!isActive) {
          setPhase('payment_pending');
        } else {
          // Funnel step 4 of 4 — the end of the landing-page funnel. Guarded so
          // the retry loop can't double-count one activation.
          if (!activationTrackedRef.current) {
            activationTrackedRef.current = true;
            track('app_subscription_activated', {
              plan_code: status.plan_code || 'creator',
              subscription_status: status.subscription_status,
              // '' / 'none' pass the isActive check too, so mark what was paid.
              is_paid: status.subscription_status === 'active' || status.subscription_status === 'trialing',
            });
          }

          // The one event the ad spend is actually judged on.
          //
          // Gated on a genuinely paid status, NOT on the isActive check above:
          // that check deliberately lets '' and 'none' through so free plans can
          // reach the product, and reporting those as Purchases would teach the
          // optimiser to find people who never pay.
          //
          // `onceEver` rather than the ref: this branch is reached by redirect
          // from Stripe and is re-run by the retry loop, so a single payment can
          // land here several times across fresh page loads.
          const isPaid =
            status.subscription_status === 'active' || status.subscription_status === 'trialing';
          if (isPaid) {
            const paidPlanCode = status.plan_code || 'creator';
            onceEver(`purchase_ws${workspaceId}_${paidPlanCode}`, () => {
              reportPurchase(paidPlanCode);
            });
          }
          setPhase('session');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not verify your subscription.');
        setPhase('error');
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, retryCount]);

  // ── Step 2: start ONBOARDING session once subscription confirmed ──────────
  useEffect(() => {
    if (phase !== 'session') return;
    if (initializingRef.current || sessionId) return;
    initializingRef.current = true;

    refreshWorkspace();

    startAgentSession(workspaceId, 'ONBOARDING')
      .then(async (res) => {
        setSessionId(res.session_id);
        try {
          const { messages: history } = await getSessionHistory(workspaceId, res.session_id);
          const visible = history.filter(
            (h) => !(h.role === 'user' && h.message.startsWith('[KICKOFF]'))
          );
          if (visible.length > 0) {
            const hydrated: ActivationMessage[] = visible.map((h) => {
              if (h.role !== 'assistant') {
                return { role: h.role, message: h.message };
              }
              const parsed = parseOutput(h.message);
              return {
                role: 'assistant' as const,
                message: parsed.message || h.message,
                agentActions: parsed.actions || [],
                suggestedReplies: parsed.suggested_replies || [],
                done: parsed.done ?? false,
              };
            });
            setMessages(hydrated);
            if (hydrated.some((m) => m.done)) {
              setDone(true);
            }
          } else {
            sendKickoff(res.session_id);
          }
        } catch {
          sendKickoff(res.session_id);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to start session.');
        setPhase('error');
        initializingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, workspaceId, sessionId]);

  // ── Output parsing ────────────────────────────────────────────────────────

  const parseOutput = (output: unknown): AgentOutputParsed => {
    const { message, actions } = parseAgentOutput(output);

    const parseIfJsonString = (value: unknown): unknown => {
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    let extra: Partial<AgentOutputParsed> = {};
    const raw = parseIfJsonString(output);
    if (raw && typeof raw === 'object') {
      const rawObj = raw as Record<string, unknown>;
      const nested = parseIfJsonString(rawObj.message);
      const nestedObj =
        nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : {};

      const pick = (key: string): unknown =>
        rawObj[key] !== undefined ? rawObj[key] : nestedObj[key];

      const replies = pick('suggested_replies');
      // Legacy sessions reported stage/complete instead of done.
      const doneFlag = pick('done') === true || pick('stage') === 'complete';
      extra = {
        suggested_replies: Array.isArray(replies)
          ? replies.filter((r): r is string => typeof r === 'string')
          : [],
        done: doneFlag,
      };
    }

    return { message, actions, ...extra };
  };

  const sendKickoff = (sid: string) => {
    runMessage(KICKOFF_MESSAGE, sid, true);
  };

  const runMessage = async (text: string, sid?: string, isSystem = false) => {
    const activeSession = sid || sessionId;
    if (!activeSession) return;

    const pendingMsg: ActivationMessage = {
      role: 'assistant',
      message: '',
      status: 'pending',
    };

    if (!isSystem) {
      const userMsg: ActivationMessage = { role: 'user', message: text };
      setMessages((prev) => [...prev, userMsg, pendingMsg]);
    } else {
      setMessages((prev) => [...prev, pendingMsg]);
    }

    setLoading(true);

    const replacePending = (msg: ActivationMessage) => {
      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].status === 'pending') {
            updated[i] = msg;
            break;
          }
        }
        return updated;
      });
    };

    try {
      const { job_id } = await sendSessionMessage(workspaceId, activeSession, text);

      setMessages((prev) => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].status === 'pending') {
            updated[i] = { ...updated[i], jobId: job_id };
            break;
          }
        }
        return updated;
      });

      pollJobStatus(
        job_id,
        getJobStatus,
        (jobStatus) => {
          const parsed = parseOutput(jobStatus.output);
          const isDone = parsed.done ?? false;

          replacePending({
            role: 'assistant',
            message: parsed.message || 'Done!',
            jobId: job_id,
            status: 'done',
            rawOutput: jobStatus.output,
            jobStatus,
            agentActions: parsed.actions || [],
            suggestedReplies: parsed.suggested_replies || [],
            done: isDone,
          });
          if (isDone) setDone(true);
          refreshWorkspace();
          setLoading(false);
        },
        (errorMsg) => {
          replacePending({
            role: 'assistant',
            message: `Something went wrong: ${errorMsg}`,
            status: 'error',
          });
          setLoading(false);
        },
        undefined,
        // Onboarding turns can render multiple images; allow up to 10 min
        // before the poll gives up (default is 5).
        600000
      );
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Unknown error';
      replacePending({
        role: 'assistant',
        message: `Something went wrong: ${errMsg}`,
        status: 'error',
      });
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!input.trim() || loading || !sessionId) return;
    const text = input.trim();
    setInput('');
    runMessage(text);
  };

  const handleChip = (text: string) => {
    if (loading || !sessionId) return;
    runMessage(text);
  };

  const handleIgConnected = (username: string) => {
    refreshWorkspace();
    runMessage(`Done — my Instagram @${username} is connected now.`);
  };

  const handleIgSkip = () => {
    runMessage("I'll connect Instagram later — let's keep going.");
  };

  // ── Brand canvas state ────────────────────────────────────────────────────

  const seenCardTypes = useMemo(() => {
    const types = new Set<string>();
    for (const m of messages) {
      for (const a of m.agentActions || []) types.add(a.type);
    }
    return types;
  }, [messages]);

  const canvasSlots: CanvasSlot[] = useMemo(
    () => [
      {
        key: 'research',
        label: 'Niche scan',
        emoji: '🔭',
        filled: seenCardTypes.has('RESEARCH_CARD'),
      },
      {
        key: 'strategy',
        label: 'Strategy',
        emoji: '🗺️',
        filled: seenCardTypes.has('STRATEGY_CARD'),
      },
      {
        key: 'colors',
        label: 'Post colors',
        emoji: '🎨',
        filled: Boolean(workspace?.theme_colors) || seenCardTypes.has('THEME_CARD'),
        colors: workspace?.theme_colors ?? null,
      },
      {
        key: 'identity',
        label: 'Name & bio',
        emoji: '🪪',
        filled:
          Boolean(workspace?.profile_bio || workspace?.profile_display_name) ||
          seenCardTypes.has('IDENTITY_CARD'),
      },
      {
        key: 'icon',
        label: 'Logo',
        emoji: '🔷',
        filled: Boolean(workspace?.brand_icon_url),
        thumb: workspace?.brand_icon_url || undefined,
      },
      {
        key: 'pfp',
        label: 'Profile picture',
        emoji: '🖼️',
        filled: Boolean(workspace?.profile_image_url),
        thumb: workspace?.profile_image_url || undefined,
        round: true,
      },
      {
        key: 'pillars',
        label: 'Pillars',
        emoji: '🏛️',
        filled: seenCardTypes.has('PILLARS_CARD'),
      },
      {
        key: 'posts',
        label: 'Scheduled posts',
        emoji: '📝',
        filled: seenCardTypes.has('OUTLINES_CARD'),
      },
      {
        key: 'first',
        label: 'First post',
        emoji: '✨',
        filled: seenCardTypes.has('CONTENT_CARD'),
      },
      {
        key: 'instagram',
        label: 'Instagram',
        emoji: '📸',
        filled: workspace ? isInstagramConnected(workspace) : false,
      },
    ],
    [seenCardTypes, workspace]
  );

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const suggestedReplies =
    !loading && !done && lastAssistant?.status !== 'pending'
      ? lastAssistant?.suggestedReplies || []
      : [];

  // ─── Render ───────────────────────────────────────────────────────────────

  if (phase === 'payment_pending') {
    return (
      <div className="act-page act-page--centered">
        <div className="act-card act-card--pending">
          <div className="act-card__icon">⏳</div>
          <h1 className="act-card__title">Payment pending…</h1>
          <p className="act-card__desc">
            We're waiting for confirmation from our payment provider. This usually takes a few
            seconds.
          </p>
          <button
            className="act-btn act-btn--secondary"
            onClick={() => setRetryCount((c) => c + 1)}
          >
            Check again
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'checking') {
    return (
      <div className="act-page act-page--centered">
        <div className="act-card">
          <div className="act-card__rocket">🚀</div>
          <h1 className="act-card__title">
            Preparing your account
            <LoadingDots />
          </h1>
          <p className="act-card__desc">Verifying your subscription…</p>
          <div className="act-progress">
            <div className="act-progress__bar" />
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="act-page act-page--centered">
        <div className="act-card act-card--error">
          <div className="act-card__icon">⚠️</div>
          <h1 className="act-card__title">Something went wrong</h1>
          <p className="act-card__desc">{error || 'Unable to start your onboarding.'}</p>
          <div className="act-ctas">
            <button
              className="act-btn act-btn--secondary"
              onClick={() => {
                setError('');
                setPhase('checking');
                setSessionId(null);
                initializingRef.current = false;
                setRetryCount((c) => c + 1);
              }}
            >
              Try again
            </button>
            <button className="act-btn act-btn--ghost" onClick={() => onNavigate('dashboard')}>
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Handoff screen — the Oracle is done, hand the account to the user ─────
  if (isComplete) {
    const igConnected = workspace ? isInstagramConnected(workspace) : false;
    const igHandle = workspace?.instagrapi_username || workspace?.instagram_handle;

    const handoffSteps: {
      key: string;
      title: string;
      desc: string;
      page: PageKey;
      done: boolean;
    }[] = [
      {
        key: 'strategy',
        title: 'Read your strategy',
        desc: 'The Oracle wrote your brand book — north star, voice, and pillars. Make sure it sounds like you.',
        page: 'strategy',
        done: false,
      },
      {
        key: 'post',
        title: 'Approve your first post',
        desc: 'Your first posts are drafted and scheduled. Review the first one before it goes out.',
        page: 'content',
        done: false,
      },
      {
        key: 'instagram',
        title: igConnected ? 'Instagram connected' : 'Connect Instagram',
        desc: igConnected
          ? `${igHandle ? `@${igHandle}` : 'Your account'} is linked — Conistra can publish for you.`
          : 'Link your account so Conistra can publish for you.',
        page: 'settings',
        done: igConnected,
      },
    ];
    if (workspace?.has_autopilot) {
      handoffSteps.push({
        key: 'autopilot',
        title: workspace?.supervisor_enabled ? 'Autopilot is on' : 'Turn on autopilot',
        desc: workspace?.supervisor_enabled
          ? 'Conistra is running the account on its own.'
          : 'Let Conistra plan, create, and publish without you lifting a finger.',
        page: 'supervisor',
        done: Boolean(workspace?.supervisor_enabled),
      });
    }

    return (
      <div className="act-page act-page--centered">
        <div className="act-handoff">
          <header className="act-handoff__head">
            <span className="act-handoff__eyebrow">Setup complete · {planConfig.name} plan</span>
            <h1 className="act-handoff__title">Your account is live.</h1>
            <p className="act-handoff__lede">
              The Oracle has built your brand and drafted your first posts
              {planConfig.hasImage ? ', cover image included' : ''}. From here, it&apos;s yours —
              start with these steps.
            </p>
          </header>

          <ol className="act-handoff__steps">
            {handoffSteps.map((step, i) => (
              <li key={step.key}>
                <button
                  type="button"
                  className={`act-handoff-step${step.done ? ' act-handoff-step--done' : ''}`}
                  onClick={() => onNavigate(step.page)}
                >
                  <span className="act-handoff-step__num" aria-hidden="true">
                    {step.done ? '✓' : String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="act-handoff-step__text">
                    <span className="act-handoff-step__title">{step.title}</span>
                    <span className="act-handoff-step__desc">{step.desc}</span>
                  </span>
                  <span className="act-handoff-step__arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ol>

          <footer className="act-handoff__foot">
            <button
              type="button"
              className="act-handoff__skip"
              onClick={() => onNavigate('dashboard')}
            >
              Skip for now — go to your dashboard →
            </button>
          </footer>
        </div>
      </div>
    );
  }

  // ── Main workshop (session phase) ─────────────────────────────────────────

  return (
    <div className="act-page act-page--wizard">
      <BrandCanvas slots={canvasSlots} planConfig={planConfig} />

      <div className="act-main">
        <div className="act-messages">
          {messages.length === 0 && (
            <div className="act-loading-init">
              <div className="act-dots-large">
                <span />
                <span />
                <span />
              </div>
              <p className="act-loading-init__text">The Oracle is on its way…</p>
            </div>
          )}

          {messages.map((msg, i) => {
            const connectActions = (msg.agentActions || []).filter(
              (a) => a.type === 'CONNECT_IG_CARD'
            );
            const cardActions = (msg.agentActions || []).filter(
              (a) => a.type !== 'CONNECT_IG_CARD' && a.type !== 'SCHEDULE_CARD'
            );
            return (
              <div
                key={i}
                className={`act-msg-row act-msg-enter ${msg.role === 'user' ? 'act-msg-row--user' : 'act-msg-row--assistant'}`}
              >
                {msg.role === 'assistant' && <div className="act-avatar">C</div>}
                <div className="act-msg-col">
                  {msg.status === 'pending' ? (
                    <PendingBubble steps={activitySteps} />
                  ) : msg.status === 'error' ? (
                    <div className="act-bubble act-bubble--error">
                      {msg.message}
                      <button
                        className="act-bubble-dismiss"
                        onClick={() => setMessages((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Dismiss
                      </button>
                    </div>
                  ) : (
                    <>
                      {msg.message && (
                        <div
                          className={`act-bubble ${msg.role === 'user' ? 'act-bubble--user' : 'act-bubble--assistant'}`}
                        >
                          {msg.message}
                        </div>
                      )}
                      {msg.role === 'assistant' && (
                        <AgentCardList
                          actions={cardActions}
                          onNavigate={onNavigate}
                          onThemeApplied={handleThemeApplied}
                        />
                      )}
                      {msg.role === 'assistant' && connectActions.length > 0 && workspace && (
                        <div className="act-connect-wrap">
                          <InstagramConnectInline
                            workspace={workspace}
                            onConnected={handleIgConnected}
                            onSkip={handleIgSkip}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {msg.role === 'user' && <div className="act-avatar act-avatar--user">You</div>}
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

        {done && !loading && (
          <div className="act-donebar">
            <span className="act-donebar__text">🎉 Your setup is complete.</span>
            <button type="button" className="act-donebar__btn" onClick={() => setPhase('success')}>
              See what&apos;s next →
            </button>
          </div>
        )}

        {suggestedReplies.length > 0 && (
          <div className="act-chips">
            {suggestedReplies.map((chip, i) => (
              <button key={i} type="button" className="act-chip" onClick={() => handleChip(chip)}>
                {chip}
              </button>
            ))}
          </div>
        )}

        <div className="act-compose">
          <input
            ref={inputRef}
            className="act-compose__input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={
              loading
                ? 'The Oracle is working…'
                : sessionId
                  ? 'Type anything — or tap a suggestion'
                  : 'Starting…'
            }
            disabled={loading || !sessionId}
          />
          <button
            className="act-compose__send"
            onClick={handleSend}
            disabled={loading || !sessionId || !input.trim()}
            aria-label="Send"
          >
            {loading ? <span className="act-compose__spinner" /> : '→'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ActivationPage;
